// src/components/stamp/Stamp.js - Complete file replacement
import React from 'react';
import EvidenceDataService from '../../services/EvidenceDataService';

/**
 * SINGLE SOURCE OF TRUTH for stamp dimensions
 * All other files should import these values
 */
export const STAMP_DIMENSIONS = {
  BASE_WIDTH: 96,
  BASE_HEIGHT: 54,
  BORDER_RADIUS: 8,
  BORDER_WIDTH: 6
};

/**
 * Get stamp dimensions at a specific scale
 */
export const getStampDimensions = (scale = 1) => {
  return {
    width: STAMP_DIMENSIONS.BASE_WIDTH * scale,
    height: STAMP_DIMENSIONS.BASE_HEIGHT * scale
  };
};

/**
 * Stamp Component - MEMOIZED to prevent infinite re-renders
 * Fixed dimensions, scaled via CSS transform
 * WITH LOGO: Logo in bottom left, text still centered ignoring logo
 */
const Stamp = React.memo(({ 
  config = {},
  documentIndex = 0, 
  caseData = null,   
  caseNumber = null, 
  scale = 1,
  className = "",
  style = {},
  ...props
}) => {
  // Validate and get stamp configuration
  const validatedConfig = EvidenceDataService.validateStampConfig(config);

  // Convert hex color to rgba with transparency from validated config
  const hexToRgba = (hex, opacity) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse hex values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Calculate the background color with transparency from validated config
  const backgroundColor = hexToRgba(validatedConfig.color, validatedConfig.opacity);
  
  // Get exhibit identifier using the data service
  const exhibitId = EvidenceDataService.getExhibitIdentifier(documentIndex, validatedConfig);
  
  // Get case number using the data service
  const displayCaseNumber = caseData 
    ? EvidenceDataService.getCaseNumber(caseData)
    : (caseNumber || "Unknown Case");
  
  // Get party type text using the data service
  const partyTypeText = EvidenceDataService.getPartyTypeText(validatedConfig);

  const { BASE_WIDTH: baseWidth, BASE_HEIGHT: baseHeight, BORDER_RADIUS: borderRadius, BORDER_WIDTH: borderWidth } = STAMP_DIMENSIONS;

  console.log('🏷️ Rendering Stamp with Logo:', {
    exhibitId,
    displayCaseNumber,
    partyTypeText,
    scale,
    baseSize: `${baseWidth}x${baseHeight}px`,
    backgroundColor: backgroundColor
  });

  return (
    <div 
      className={`stamp-component ${className}`}
      style={{
        // CHANGED: Fixed pixel dimensions instead of 100%
        width: `${baseWidth}px`,
        height: `${baseHeight}px`,
        backgroundColor: backgroundColor,
        border: `${borderWidth}px solid #000000`,
        borderRadius: `${borderRadius}px`,
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontWeight: 'bold',
        color: '#000000',
        lineHeight: 1,
        boxSizing: 'border-box',
        position: 'relative',
        display: 'flex',
        overflow: 'hidden',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        ...style
      }}
      {...props}
    >
      {/* Logo in bottom left corner - absolutely positioned so it doesn't affect text centering */}
      <img 
        src="/stamplogo.svg"
        alt="Logo"
        style={{
          position: 'absolute',
          bottom: '2px',    // Same as bottom padding of content area
          right: '2px',      // Same as left padding of content area
          width: '10px',    // Fixed size that fits nicely in corner
          height: '13px',   
          objectFit: 'contain',
          zIndex: 1,        // Behind text content
          opacity: 1,
          imageRendering: 'auto',                // Let browser optimize for SVG
          shapeRendering: 'geometricPrecision'   // SVG-specific crisp rendering
        }}
      />

      {/* Main content area - UNCHANGED flex layout for perfect text centering */}
      <div 
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `3px 3px 3px 3px`,
          textAlign: 'center',
          zIndex: 2,        // Above logo
          boxSizing: 'border-box'
        }}
      >
        {/* Top section - Party type and "EXHIBIT" text */}
        <div 
          style={{ 
            fontSize: '9px',
            lineHeight: 1,
            fontWeight: 'bold',
            textAlign: 'center',
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#000000'
          }}
        >
          <div>{partyTypeText.toUpperCase()}'S</div>
          <div>EXHIBIT</div>
        </div>

        {/* Middle section - Large exhibit letter/number */}
        <div 
          style={{ 
            fontSize: '8px',
            fontWeight: 'normal',
            lineHeight: 0.5,
            textAlign: 'center',
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#000000',
            margin: '2px 0'
          }}
        >
          {exhibitId}
        </div>

        {/* NEW: Horizontal line between exhibit number and case number */}
        <div 
          style={{
            width: '60%',           // Line spans 70% of the stamp width
            height: '1px',          // Thin line
            backgroundColor: '#000000ff',  // Black line
            margin: '1px 0',        // Small margin above and below
            alignSelf: 'center'     // Center the line horizontally
          }}
        />

        {/* Bottom section - Case number */}
        <div 
          style={{ 
            fontSize: '7px',
            lineHeight: 0.75,
            fontWeight: 'normal',
            textAlign: 'center',
            fontFamily: 'Helvetica, Arial, sans-serif',
            color: '#000000'
          }}
        >
          {displayCaseNumber}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // CUSTOM COMPARISON FUNCTION for React.memo
  // Only re-render if these specific values actually change
  return (
    prevProps.documentIndex === nextProps.documentIndex &&
    prevProps.scale === nextProps.scale &&
    prevProps.className === nextProps.className &&
    prevProps.caseNumber === nextProps.caseNumber &&
    // Deep compare config object
    JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config) &&
    // Deep compare caseData object
    JSON.stringify(prevProps.caseData) === JSON.stringify(nextProps.caseData) &&
    // Deep compare style object
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
  );
});

// Set display name for debugging
Stamp.displayName = 'Stamp';

export default Stamp;